import React, { memo, forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react'
import * as echarts from 'echarts'
import 'echarts-wordcloud'
import { LineChart, BarChart, PieChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LegendScrollComponent,
  LegendPlainComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
} from 'echarts/components'
import {
  CanvasRenderer,
} from 'echarts/renderers'
import classnames from 'classnames'
import config from './config'
import './index.less'

echarts.use(
  [TitleComponent, TooltipComponent, GridComponent, LegendComponent,
    LegendScrollComponent,
    LegendPlainComponent,
    DataZoomComponent,
    DataZoomInsideComponent,
    DataZoomSliderComponent,
    DatasetComponent,
    LineChart, BarChart, PieChart,
    CanvasRenderer]
)

/**
   *
   * @param {object} props
   * @param {object} props.dataset - 数据
   * @param {[][]} props.dataset.source - 第一行维度可传可不传
   * @param {'line'|'bar'|'pie'} props.type - 图表类型
   * @param {string} props.title - 图表名称
   * @param {string|JSX.Element} props.headerSlot - 图表头部插槽
   * @param {string|JSX.Element} props.contentSlot - 图表内容插槽
   * @param {string} props.className - 容器样式
   * @param {object} props.customConfig - 自定义图表配置
   */

export default memo(forwardRef(({
  dataset = { source: [] }, notMerge = false, replaceMerge, lazyUpdate, type = 'line', title = '',
  headerSlot, contentSlot, customConfig, className = '', colors, id, ...otherProps
}, ref) => {
  const { showEmpty, emptySlot, onClickSeries } = otherProps
  const $chart = useRef()
  const $chartDom = useRef()
  const _config = useMemo(() => config(type, customConfig?.dataset || dataset, customConfig, colors), [type, dataset, customConfig, colors])

  useImperativeHandle(ref, () => ({
    getEchartsInstance: () => $chart.current,
  }))

  useEffect(() => {
    $chart.current = echarts.init($chartDom.current)
    $chart.current.on('click', 'series', onClickSeries)
    return () => {
      $chart.current.off('click')
    }
  }, [])

  useEffect(() => {
    _config && $chart.current.setOption(_config, {
      notMerge,
      replaceMerge,
      lazyUpdate
    })
  }, [_config])

  return (
    <div className={classnames('common-chart-wrapper', className)} {...otherProps}>
      {!!title && (
      <div className="common-chart-header">
        <span className="title">{title}</span>
        {headerSlot}
      </div>
      )}
      <div style={{ flex: '1', position: 'relative' }}>
        <div style={{ width: '100%', height: '100%', visibility: showEmpty ? 'hidden' : 'visible' }} ref={$chartDom} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, visibility: showEmpty ? 'visible' : 'hidden' }}>
          {emptySlot}
        </div>
      </div>
      <div>{contentSlot}</div>
    </div>
  )
}))
